import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  fetchLatestCmsNews,
  normalizeCmsPostsForLegacyView,
} from "../../api/cmsClient";
import { getCmsLocaleFromLanguage } from "../../api/cmsLocale";
import {
  cacheCmsLatestPosts,
  fetchLocalNews,
  getCachedCmsLatestPosts,
} from "../../api/newsFallback";

const latestNewsLimit = 3;

function slugify(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/gi, "dj")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

function getNewsSlug(post) {
  return post?.attributes?.Slug || slugify(post?.attributes?.Title || "novost");
}

function getNewsPath(post) {
  if (post?.routePath) return post.routePath;
  return `/novosti/${post.id}-${getNewsSlug(post)}`;
}

function getPostImage(post) {
  return post?.attributes?.Image || "/assets/img/logo/fpepblog.png";
}

function getPostExcerpt(post, maxLength = 115) {
  const description = post?.attributes?.Description || "";
  const normalized = String(description).replace(/\s+/g, " ").trim();

  if (normalized.length <= maxLength) return normalized;
  return `${normalized.substring(0, maxLength).trim()}...`;
}

function LatestNewsSkeleton() {
  return (
    <div className="row justify-content-center">
      {[0, 1, 2].map((item) => (
        <div className="col-lg-4 col-md-6 mb-30" key={item}>
          <div className="fpep-home-news-card fpep-home-news-card--loading" aria-hidden="true">
            <div className="fpep-home-news-card__image" />
            <div className="fpep-home-news-card__body">
              <span className="fpep-home-news-card__line fpep-home-news-card__line--short" />
              <span className="fpep-home-news-card__line fpep-home-news-card__line--title" />
              <span className="fpep-home-news-card__line" />
              <span className="fpep-home-news-card__line" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function LatestNews() {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const cmsLocale = getCmsLocaleFromLanguage(i18n.language);
  const isEnglish = cmsLocale === "en";

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const applyPosts = (items = []) => {
      if (!isMounted) return;
      setPosts(items.slice(0, latestNewsLimit));
    };

    const loadLocalFallback = async () => {
      const localPosts = await fetchLocalNews();
      applyPosts(localPosts.slice(0, latestNewsLimit));
    };

    const loadLatestNews = async () => {
      setLoading(true);

      try {
        const result = await fetchLatestCmsNews({
          limit: latestNewsLimit,
          locale: cmsLocale,
          signal: controller.signal,
        });
        const cmsPosts = normalizeCmsPostsForLegacyView(result.data || []);

        if (cmsPosts.length) {
          cacheCmsLatestPosts(latestNewsLimit, cmsPosts, cmsLocale);
          applyPosts(cmsPosts);
          return;
        }

        await loadLocalFallback();
      } catch (error) {
        if (error?.name === "AbortError") return;

        const cachedPosts = getCachedCmsLatestPosts(latestNewsLimit, cmsLocale);

        if (cachedPosts.length) {
          applyPosts(cachedPosts);
          return;
        }

        try {
          await loadLocalFallback();
        } catch (fallbackError) {
          console.warn("Homepage latest news fallback failed.", fallbackError);
          applyPosts([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadLatestNews();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [cmsLocale]);

  if (!loading && posts.length === 0) {
    return null;
  }

  return (
    <section className="fpep-home-news-area pt-110 pb-90 p-relative fix">
      <div className="animations-03">
        <img src="assets/img/bg/floating-logo.png" alt="floating-logo-fpep" />
      </div>

      <div className="container">
        <div className="row align-items-end mb-45">
          <div className="col-lg-8 col-md-8">
            <div className="section-title wow fadeInDown animated" data-animation="fadeInDown" data-delay=".4s">
              <h5>
                <i className="fas fa-newspaper" /> {isEnglish ? "News" : "Novosti"}
              </h5>
              <h2>{isEnglish ? "Latest news" : "Najnovije objave"}</h2>
              <p className="fpep-home-news-area__lead">
                {isEnglish
                  ? "Follow the latest announcements, events and activities from the Faculty."
                  : "Pratite najnovija obavještenja, događaje i aktivnosti Fakulteta."}
              </p>
            </div>
          </div>
          <div className="col-lg-4 col-md-4 text-md-right mt-25 mt-md-0">
            <Link to="/novosti" className="btn ss-btn fpep-home-news-area__btn">
              {isEnglish ? "All news" : "Sve novosti"} <i className="fal fa-long-arrow-right" />
            </Link>
          </div>
        </div>

        {loading ? (
          <LatestNewsSkeleton />
        ) : (
          <div className="row justify-content-center">
            {posts.map((post) => (
              <div
                className="col-lg-4 col-md-6 mb-30 d-flex align-items-stretch"
                key={`${post.source || "local"}-home-${post.cmsId || post.id}`}
              >
                <article className="fpep-home-news-card hover-zoomin">
                  <Link to={getNewsPath(post)} className="fpep-home-news-card__thumb" aria-label={post.attributes.Title}>
                    <img
                      src={getPostImage(post)}
                      alt={post.attributes.Title || "FPEP novost"}
                      loading="lazy"
                      onError={(event) => {
                        event.currentTarget.src = "/assets/img/logo/fpepblog.png";
                      }}
                    />
                    {post.attributes.Date && (
                      <span className="fpep-home-news-card__date">
                        <i className="fas fa-calendar-alt" /> {post.attributes.Date}
                      </span>
                    )}
                  </Link>

                  <div className="fpep-home-news-card__body">
                    <h3>
                      <Link to={getNewsPath(post)}>{post.attributes.Title}</Link>
                    </h3>
                    <p>{getPostExcerpt(post)}</p>
                    <Link to={getNewsPath(post)} className="readmore fpep-home-news-card__readmore">
                      {t("header.SaznajVise")} <i className="fas fa-arrow-right" />
                    </Link>
                  </div>
                </article>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default LatestNews;
